import { CodeBlock } from '@crowdex/ui/ai/code-block';
import { MessageResponse } from '@crowdex/ui/ai/message';
import { Button } from '@crowdex/ui/components/button';
import { Dialog, DialogContent, DialogTitle } from '@crowdex/ui/components/dialog';
import { Input } from '@crowdex/ui/components/input';
import { toast } from '@crowdex/ui/components/toaster';
import {
  ArrowLeft,
  ChevronRight,
  File,
  FileCode2,
  Folder,
  FolderOpen,
  LoaderCircle,
  RotateCw,
  Search,
  X,
} from 'lucide-react';
import { type ComponentProps, Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import type { FileSelection } from '@/components/desktop-side-panel/file-selection-toolbar';
import { useLongPress } from '@/components/mobile-shell/use-long-press';
import { basename, codeLanguage, pathSegments } from '@/lib/desktop-paths';
import { useWorkspaceName } from '@/lib/desktop-shell/use-workspace-name';
import type { WorkspaceEntry } from '@/lib/native-desktop';
import { isCodeFile } from '@/lib/side-panel/file-explorer';
import { selectionWithin, useTouchSelection } from '@/lib/side-panel/text-selection';

/** A workspace file opened in the viewer pane. */
export type OpenedWorkspaceFile =
  | { path: string; kind: 'text'; content: string }
  | { path: string; kind: 'media'; url: string; mediaType: string };

/**
 * The Files tab: a workspace explorer with a preview pane. Narrow panels use a
 * list-to-preview navigation, while wider panels show both panes side by side.
 * All file-system access and selection handling is delegated to the owning
 * `SidePanel` through callbacks.
 */
export function FilesView({
  workspace,
  activeFile,
  showFileSource,
  onToggleFileSource,
  fileFilter,
  onFileFilterChange,
  onClearFileFilter,
  filesError,
  fileEntries,
  expandedDirectories,
  loadingDirectories,
  onRefreshFiles,
  onToggleDirectory,
  onOpenFile,
  onSelectText,
  onMentionFile,
  editor,
}: {
  /** Writes `@path` in the conversation's composer (D34 "Mention in chat"). */
  onMentionFile?: ((path: string) => void) | undefined;
  /** The user's editor, to open the file in ("Open in VS Code"). */
  editor?: { name: string; open: (path: string) => void } | undefined;
  workspace: string | null;
  activeFile: OpenedWorkspaceFile | null;
  showFileSource: boolean;
  onToggleFileSource: () => void;
  fileFilter: string;
  onFileFilterChange: (value: string) => void;
  onClearFileFilter: () => void;
  filesError: string | null;
  fileEntries: Record<string, WorkspaceEntry[]>;
  expandedDirectories: Set<string>;
  loadingDirectories: Set<string>;
  onRefreshFiles: () => void;
  onToggleDirectory: (path: string) => void;
  onOpenFile: (path: string) => void;
  onSelectText: (selection: FileSelection) => void;
}) {
  const workspaceName = useWorkspaceName(workspace);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(activeFile !== null);
  // A file long-pressed on a phone (mobile 31): its actions in a sheet.
  const [pressedFile, setPressedFile] = useState<string | null>(null);
  // A finger's selection in the file (its handles, no mouseup) offers its actions too.
  const contentRef = useRef<HTMLElement>(null);
  useTouchSelection((selection, range) => {
    if (contentRef.current?.contains(range.commonAncestorContainer)) onSelectText(selection);
  });

  useEffect(() => {
    setMobilePreviewOpen(activeFile !== null);
  }, [activeFile]);

  function fileIcon(entry: WorkspaceEntry): ReactNode {
    if (entry.isDirectory) {
      return expandedDirectories.has(entry.path) ? (
        <FolderOpen className="size-4 shrink-0 text-muted-foreground" />
      ) : (
        <Folder className="size-4 shrink-0 text-muted-foreground" />
      );
    }
    if (isCodeFile(entry.name)) {
      return <FileCode2 className="size-4 shrink-0 text-muted-foreground" />;
    }
    return <File className="size-4 shrink-0 text-muted-foreground" />;
  }

  function renderFileEntries(directoryPath = '', depth = 0): ReactNode {
    const filter = fileFilter.trim().toLowerCase();
    const entries = (fileEntries[directoryPath] ?? []).filter(
      (entry) =>
        filter.length === 0 || entry.name.toLowerCase().includes(filter) || entry.isDirectory,
    );
    return entries.map((entry) => {
      const expanded = entry.isDirectory && expandedDirectories.has(entry.path);
      const children = expanded ? renderFileEntries(entry.path, depth + 1) : null;
      const hideEmptyFilteredDirectory =
        filter.length > 0 && entry.isDirectory && expanded && children === null;
      if (hideEmptyFilteredDirectory) return null;
      return (
        <Fragment key={entry.path}>
          <FileEntryButton
            className={`type-ui group flex h-control-sm w-full items-center gap-1.5 rounded-md pr-2 text-left transition-colors hover:bg-accent/70 max-sm:h-11 ${
              activeFile?.path === entry.path ? 'bg-accent text-foreground' : 'text-foreground/85'
            }`}
            onClick={() => {
              if (entry.isDirectory) onToggleDirectory(entry.path);
              else onOpenFile(entry.path);
            }}
            onLongPress={entry.isDirectory ? undefined : () => setPressedFile(entry.path)}
            style={{ paddingLeft: `${8 + depth * 18}px` }}
          >
            {entry.isDirectory ? (
              loadingDirectories.has(entry.path) ? (
                <LoaderCircle className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
              ) : (
                <ChevronRight
                  className={`size-3.5 shrink-0 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`}
                />
              )
            ) : (
              <span className="w-3.5 shrink-0" />
            )}
            {fileIcon(entry)}
            <span className="min-w-0 flex-1 truncate">{entry.name}</span>
          </FileEntryButton>
          {expanded && children}
        </Fragment>
      );
    });
  }

  return (
    <div className="flex h-full min-h-0 min-w-0">
      {/* D34: the tree on the left, what it opens on the right. */}
      <aside
        className={`min-h-0 min-w-56 max-w-80 flex-1 flex-col border-hairline-strong border-r bg-background max-sm:min-w-0 max-sm:max-w-none max-sm:border-r-0 ${mobilePreviewOpen ? 'flex max-sm:hidden' : 'flex'}`}
        data-mobile-view="file-explorer"
      >
        <div className="flex shrink-0 items-center gap-2 p-2">
          <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-border bg-card px-3 focus-within:border-ring">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <Input
              aria-label="Filter files"
              appearance="bare"
              className="h-auto min-w-0 flex-1 p-0 text-sm placeholder:text-muted-foreground"
              onChange={(event) => onFileFilterChange(event.target.value)}
              placeholder="Filter files…"
              value={fileFilter}
            />
            {fileFilter.length > 0 && (
              <button
                aria-label="Clear file filter"
                className="rounded p-0.5 text-muted-foreground hover:text-foreground"
                onClick={onClearFileFilter}
                type="button"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
          <Button
            aria-label="Refresh files"
            onClick={onRefreshFiles}
            size="icon-sm"
            variant="ghost"
          >
            <RotateCw className="size-3.5" />
          </Button>
        </div>
        <FilesBreadcrumb expandedDirectories={expandedDirectories} workspace={workspace} />
        <div className="min-h-0 flex-1 overflow-auto px-1.5 pb-2">
          {filesError !== null ? (
            <p className="p-3 text-destructive text-xs">{filesError}</p>
          ) : loadingDirectories.has('') && fileEntries[''] === undefined ? (
            <div className="flex items-center gap-2 p-3 text-muted-foreground text-xs">
              <LoaderCircle className="size-3.5 animate-spin" />
              Loading files…
            </div>
          ) : (
            renderFileEntries()
          )}
        </div>
      </aside>
      <section
        className={`min-h-0 min-w-0 flex-[2] flex-col ${mobilePreviewOpen ? 'flex' : 'flex max-sm:hidden'}`}
        data-mobile-view="file-preview"
      >
        {activeFile === null ? (
          <div className="flex min-h-0 flex-1 items-center justify-center text-muted-foreground text-sm">
            Choose a file from the explorer
          </div>
        ) : (
          <>
            <div className="flex h-11 shrink-0 items-center gap-2 border-hairline-strong border-b px-4 text-xs">
              <button
                aria-label="Back to file explorer"
                className="hidden shrink-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-muted-foreground hover:bg-accent hover:text-foreground max-sm:flex"
                onClick={() => setMobilePreviewOpen(false)}
                type="button"
              >
                <ArrowLeft className="size-3.5" />
                Files
              </button>
              <span className="min-w-0 flex-1 truncate font-medium sm:hidden">
                {basename(activeFile.path)}
              </span>
              <span className="hidden min-w-0 flex-1 items-center gap-2 overflow-hidden sm:flex">
                <span className="text-muted-foreground">{workspaceName}</span>
                {pathSegments(activeFile.path).map((part, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: A path can repeat a folder name (src/app/src): segments are positions.
                  <Fragment key={index}>
                    <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate last:text-foreground">{part}</span>
                  </Fragment>
                ))}
              </span>
              {activeFile.kind === 'text' && activeFile.path.toLowerCase().endsWith('.md') && (
                <button
                  className="ml-auto shrink-0 text-muted-foreground hover:text-foreground"
                  onClick={onToggleFileSource}
                  type="button"
                >
                  {showFileSource ? 'View preview' : 'View source'}
                </button>
              )}
              {onMentionFile !== undefined && (
                <Button
                  className="h-7 shrink-0 rounded-control px-3 text-xs"
                  onClick={() => onMentionFile(activeFile.path)}
                  size="sm"
                  variant="outline"
                >
                  Mention in chat
                </Button>
              )}
              {/* An editor of this computer: a phone has none, and the file's name needs the room. */}
              {editor !== undefined && (
                <Button
                  className="h-7 shrink-0 rounded-control px-3 text-xs max-sm:hidden"
                  onClick={() => editor.open(activeFile.path)}
                  size="sm"
                  variant="outline"
                >
                  Open in {editor.name}
                </Button>
              )}
            </div>
            <section
              aria-label={`File content: ${activeFile.path}`}
              className="min-h-0 flex-1 overflow-auto"
              onMouseUp={(event) => {
                const found = selectionWithin(event.currentTarget);
                if (found !== null) onSelectText(found.selection);
              }}
              ref={contentRef}
            >
              {activeFile.kind === 'media' && activeFile.mediaType.startsWith('image/') ? (
                <div className="flex min-h-full items-center justify-center bg-black/5 p-5">
                  <img
                    alt={basename(activeFile.path)}
                    className="max-h-full max-w-full object-contain"
                    src={activeFile.url}
                  />
                </div>
              ) : activeFile.kind === 'media' && activeFile.mediaType.startsWith('video/') ? (
                <div className="flex min-h-full items-center justify-center bg-black p-5">
                  {/* biome-ignore lint/a11y/useMediaCaption: arbitrary workspace videos have no caption track to attach. */}
                  <video
                    className="max-h-full max-w-full"
                    controls
                    preload="metadata"
                    src={activeFile.url}
                  >
                    Your browser cannot play this video format.
                  </video>
                </div>
              ) : activeFile.kind === 'text' &&
                activeFile.path.toLowerCase().endsWith('.md') &&
                !showFileSource ? (
                <MessageResponse className="type-ui mx-auto max-w-3xl px-6 py-5 text-foreground/95 leading-6 [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-base">
                  {activeFile.content}
                </MessageResponse>
              ) : activeFile.kind === 'text' ? (
                <CodeBlock
                  className="min-h-full rounded-none border-0 [&_.line>span:first-child]:mr-2! [&_.line>span:first-child]:min-w-7! [&_code]:type-caption! [&_pre]:px-2! [&_pre]:py-3! [&_pre]:type-caption!"
                  code={activeFile.content}
                  language={codeLanguage(activeFile.path)}
                  showLineNumbers
                />
              ) : null}
            </section>
          </>
        )}
      </section>
      {pressedFile !== null && (
        <FileActionsSheet
          onClose={() => setPressedFile(null)}
          onMentionFile={onMentionFile}
          path={pressedFile}
        />
      )}
    </div>
  );
}

/** A row of the explorer: a tap opens it, a long press (a file, on a phone) offers its actions. */
function FileEntryButton({
  onLongPress,
  ...props
}: Omit<ComponentProps<'button'>, 'type'> & { onLongPress?: (() => void) | undefined }) {
  const press = useLongPress(() => onLongPress?.());
  return <button type="button" {...props} {...(onLongPress === undefined ? {} : press)} />;
}

/**
 * Where the explorer is, on a phone (mobile 31): the repository, then the folders down to the
 * deepest one unfolded ("crowdex / apps / web / src / components").
 */
export function filesBreadcrumb(
  /** The workspace's name (the server's for its folder). */
  workspaceName: string | null,
  expandedDirectories: ReadonlySet<string>,
): string[] {
  let deepest = '';
  for (const directory of expandedDirectories) {
    const parts = directory.split('/');
    // Only an unfolded chain counts: each of its parents unfolded too.
    const chain = parts.every((_, index) =>
      expandedDirectories.has(parts.slice(0, index + 1).join('/')),
    );
    if (chain && parts.length > (deepest === '' ? 0 : deepest.split('/').length))
      deepest = directory;
  }
  return [workspaceName ?? '', ...(deepest === '' ? [] : deepest.split('/'))].filter(
    (part) => part !== '',
  );
}

function FilesBreadcrumb({
  workspace,
  expandedDirectories,
}: {
  workspace: string | null;
  expandedDirectories: ReadonlySet<string>;
}) {
  const parts = filesBreadcrumb(useWorkspaceName(workspace), expandedDirectories);
  if (parts.length === 0) return null;
  return (
    <p
      className="type-caption hidden truncate px-3 pb-1.5 font-mono text-muted-foreground max-sm:block"
      data-files-breadcrumb
    >
      {parts.join(' / ')}
    </p>
  );
}

/** A long-pressed file's actions (mobile 31): Mention in chat, Copy path. */
function FileActionsSheet({
  path,
  onMentionFile,
  onClose,
}: {
  path: string;
  onMentionFile?: ((path: string) => void) | undefined;
  onClose: () => void;
}) {
  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open>
      <DialogContent
        aria-describedby={undefined}
        className="top-auto bottom-0 left-0 flex w-full max-w-full translate-x-0 translate-y-0 flex-col gap-3 rounded-b-none rounded-t-window bg-popover p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:max-w-full"
        data-sheet
        showCloseButton={false}
      >
        <div>
          <p className="type-meta text-muted-foreground">Long-press a file</p>
          <DialogTitle className="truncate font-mono font-semibold text-base">
            {basename(path)}
          </DialogTitle>
        </div>
        <div className="flex gap-2">
          {onMentionFile !== undefined && (
            <Button
              className="h-11 flex-1 rounded-control"
              onClick={() => {
                onClose();
                onMentionFile(path);
              }}
              variant="outline"
            >
              Mention in chat
            </Button>
          )}
          <Button
            className="h-11 flex-1 rounded-control"
            onClick={() => {
              onClose();
              void navigator.clipboard
                ?.writeText(path)
                .then(() => toast.success('Path copied'))
                .catch(() => toast.error('Couldn’t copy the path'));
            }}
            variant="outline"
          >
            Copy path
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
